import {
  Box,
  Button,
  FormatNumber,
  HStack,
  Image,
  Spacer,
  Stack,
  Text,
  VStack,
  chakra,
} from '@chakra-ui/react';
import { useI18nLn } from '~/hooks/store';
import { DeliveryBundle, PlayTicket, ShippingSpec } from '~/types/resources';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import { GachaDetail } from '~/types/resources/gachaDetail';
import { EventRemainDeliveryCount } from '../event/EventRemainDeliveryCount';

type Props = {
  bundleAmount: number;
  discountedPrice: number;
  playAmount: number;
  isPlayTicketPayment: boolean;
  skipDelivery: boolean;
  deliveryInfoOnOpen: () => void;
  deliveryBundles: DeliveryBundle[];
  deliveryInfo: DeliveryInfo | null;
  gachaDetail: GachaDetail;
  selectedPlayTicket: PlayTicket | null;
  shippingSpec: ShippingSpec | undefined;
  deliveryCharge: number;
};

export const BillingDetail: React.FC<Props> = (props: Props) => {
  const { t } = useI18nLn();

  /**
   * 現在の注文がおまとめ配送であるかを示す
   * @returns boolean
   */
  const isBundledShipping = () => {
    return (
      props.shippingSpec?.bundle_capacity &&
      props.shippingSpec?.bundle_capacity > 0
    );
  };

  return (
    <VStack w="full" justify="space-between" align="flex-end">
      <VStack
        w="full"
        justify="space-between"
        alignItems="flex-end"
        hidden={props.skipDelivery}
      >
        {isBundledShipping() &&
        props.bundleAmount > 0 &&
        props.gachaDetail.gacha_delivery_info?.charge ? (
          <HStack w="full" justify="space-between" align="flex-end">
            <Text fontSize="sm">{t('global.common.delivery_fee')}</Text>
            <HStack>
              <Text mr={2} fontSize="md">
                {t('components.shipments.about_bundle_capacity', {
                  amount: props.shippingSpec?.bundle_capacity,
                })}
              </Text>
              <Text fontSize="lg">
                &yen;
                <FormatNumber
                  value={props.gachaDetail.gacha_delivery_info.charge}
                />
                ×<chakra.span fontSize="md">{props.bundleAmount}</chakra.span>
              </Text>
            </HStack>
          </HStack>
        ) : (
          <>
            <HStack w="full" justify="space-between" align="flex-end">
              <Text fontSize="sm">{t('global.common.delivery_fee')}</Text>
              <Text fontSize="xl">
                &yen;
                <FormatNumber value={props.deliveryCharge} />
              </Text>
            </HStack>
          </>
        )}
      </VStack>
      <Stack w="fit-content" align="flex-end">
        <EventRemainDeliveryCount
          playAmount={props.playAmount}
          deliveryBundles={props.deliveryBundles}
          deliveryInfo={props.deliveryInfo}
          country={props.deliveryInfo?.country ?? 'JP'}
        />
      </Stack>
      <Button
        h="24px"
        fontSize="10px"
        bgColor="blank"
        color="link"
        onClick={props.deliveryInfoOnOpen}
      >
        {t('pages.gacha.delivery.detail')}
        <Box w="4px" />
        <Image src="/assets/icon/hatena.svg" alt="icon" h="12px" w="12px" />
      </Button>
      {props.isPlayTicketPayment && (
        <HStack gap={0} alignItems="end">
          <Text fontSize="13px">
            {t('components.order_info.play_ticket')}
            {props.selectedPlayTicket?.name}
          </Text>
          <Spacer />
          <HStack gap={0} alignItems="end" fontSize="24px">
            <Text fontSize="13px">&yen;</Text>
            <Text mb={-1} fontWeight="bold">
              -<FormatNumber value={props.discountedPrice} />
            </Text>
          </HStack>
        </HStack>
      )}
    </VStack>
  );
};
